import * as React from 'react';
import {Box} from '@mui/material';
import {ReportDataType} from "pages/ReportsPage";
import {decimalNumber} from "../../helpers/number";
import {periodToGui} from "../../helpers/date";
import {useAppTranslation} from "../../services/i18n";
import type {ReportingStudyTimesGroupedStudentsStudentsResponseRecordsInnerPeriodsInner} from "generated-api";

interface StudyTimesContentsTableProps {
    data: ReportDataType['studyTimesContents'] | ReportDataType['studyTimesStudents'];
    type: 'studyTimesContents' | 'studyTimesStudents';
}

const formatNumber = (value: number | null | undefined, suffix: string = '', minDigits: number = 0, maxDigits: number = 2) => {
    if (value === null || value === undefined) {
        return null;
    }
    return `${decimalNumber(value, minDigits, maxDigits)}${suffix}`;
};


export const ReportStudyTimes: React.FC<StudyTimesContentsTableProps> = ({data, type}) => {
    const t = useAppTranslation();

    if (!data?.periods?.length) {
        return null;
    }

    const periods = data.periods;
    const records = data.records || [];

    const contentRecordsMap = new Map<number, Map<string, ReportingStudyTimesGroupedStudentsStudentsResponseRecordsInnerPeriodsInner>>();

    records.forEach(record => {
        const key = 'content_id' in record ? record.content_id : record.user_id;
        if (!contentRecordsMap.has(key)) {
            contentRecordsMap.set(key, new Map());
        }
        record.periods.forEach(period => {
            contentRecordsMap.get(key)!.set(period.period_key, period);
        });
    });

    const middleGridColumns = `repeat(${periods.length * 4}, 120px)`;

    return <Box sx={{
        display: 'flex',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: 'var(--color-white)'
    }}>
        {/* Left sticky column */}
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: '200px',
            maxWidth: '200px',
            flexShrink: 0,
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: 'var(--color-white)'
        }}>
            <Box sx={{padding: '10px', fontWeight: 600, fontSize: '18px', color: '#8D8D8D'}}>
                &nbsp;
            </Box>
            <Box sx={{padding: '8px', color: '#8D8D8D', borderBottom: '1px solid #595959', fontSize: '14px', height: 60}}>
                {type === 'studyTimesStudents' ? t('reports.table.studentName') : t('reports.table.contentName')}
            </Box>
            {records.map((totalRecord, index) => {
                const isOdd = index % 2 === 1;
                const bgColor = isOdd ? '#FAFAFA' : 'var(--color-white)';
                const key = 'content_id' in totalRecord ? totalRecord.content_id : totalRecord.user_id;
                const title = 'content_name' in totalRecord ? totalRecord.content_name : totalRecord.full_name;
                return (
                    <Box
                        key={`left-${key}-${index}`}
                        sx={{
                            backgroundColor: bgColor,
                            color: '#595959',
                            padding: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        title={title}
                    >
                        {title}
                    </Box>
                );
            })}
        </Box>

        {/* Middle scrollable section */}
        <Box sx={{
            overflow: 'auto',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 'fit-content',
            }}>
                {/* Header row 1 */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: middleGridColumns
                }}>
                    {periods.map(period => (
                        <Box
                            key={period.period_key}
                            sx={{
                                gridColumn: 'span 4',
                                padding: '10px',
                                textAlign: 'center',
                                fontWeight: 600,
                                fontSize: '18px',
                                color: '#8D8D8D'
                            }}
                        >
                            {periodToGui(period.period_key)}
                        </Box>
                    ))}
                </Box>

                {/* Header row 2 */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: middleGridColumns,
                    '& > div': {
                        padding: '8px',
                        color: '#8D8D8D',
                        fontSize: '14px',
                        height: 60,
                        borderBottom: '1px solid #595959'
                    }
                }}>
                    {periods.map((period) => {
                        return (
                            <React.Fragment key={`headers-${period.period_key}`}>
                                <Box>
                                    {t('reports.table.testSuccessRate')}<br/>({t('reports.table.percentFirstAttempt')})
                                </Box>
                                <Box>
                                    {t('reports.table.studyDuration')}<br/>({t('reports.table.avgMinutes')})
                                </Box>
                                <Box>
                                    {t('reports.table.studyCount')}
                                </Box>
                                <Box>
                                    {t('reports.table.stepCount')}
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>

                {/* Data rows */}
                {records.map((totalRecord, index) => {
                    const key = 'content_id' in totalRecord ? totalRecord.content_id : totalRecord.user_id;
                    const recordsByPeriod = contentRecordsMap.get(key);
                    const isOdd = index % 2 === 1;
                    const bgColor = isOdd ? '#FAFAFA' : 'var(--color-white)';

                    return <Box
                        key={`middle-${key}-${index}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: middleGridColumns,
                            '& > div': {
                                color: '#595959',
                                padding: '8px',
                            }
                        }}
                    >
                        {periods.map((period) => {
                            const record = recordsByPeriod?.get(period.period_key);

                            return (
                                <React.Fragment key={`${key}-${period.period_key}`}>
                                    <Box sx={{backgroundColor: bgColor, borderLeft: '1px solid silver'}}>
                                        {formatNumber(record?.avg_result_rate, ' %', 2, 2)}
                                    </Box>
                                    <Box sx={{backgroundColor: bgColor}}>
                                        {formatNumber(record?.avg_study_time_minutes, '', 2, 2)}
                                    </Box>
                                    <Box sx={{backgroundColor: bgColor}}>
                                        {formatNumber(record?.total_studies)}
                                    </Box>
                                    <Box sx={{backgroundColor: bgColor}}>
                                        {formatNumber(record?.total_step_count)}
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                    </Box>;
                })}
            </Box>
        </Box>

        {/* Right sticky column */}
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 'fit-content',
            minWidth: 'calc(4 * 110px)',
            flexShrink: 0,
            // flexGrow: 1,
            position: 'sticky',
            right: 0,
            zIndex: 2,
            backgroundColor: 'var(--color-white)',
        }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, auto)'
            }}>
                <Box sx={{
                    gridColumn: 'span 4',
                    padding: '10px',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '18px',
                    color: 'var(--color-black)',
                }}>
                    {t('reports.table.totalSum')}
                </Box>
                <Box sx={{
                    gridColumn: 'span 4',
                    display: 'grid',
                    gridTemplateColumns: 'inherit',
                    '& > div': {
                        padding: '8px',
                        fontSize: '14px',
                        height: 60,
                        borderBottom: '1px solid #595959'
                    }
                }}>
                    <Box>
                        {t('reports.table.testSuccessRate')}<br/>({t('reports.table.percentFirstAttempt')})
                    </Box>
                    <Box>
                        {t('reports.table.studyDuration')}<br/>({t('reports.table.avgMinutes')})
                    </Box>
                    <Box>
                        {t('reports.table.studyCount')}
                    </Box>
                    <Box>
                        {t('reports.table.stepCount')}
                    </Box>
                </Box>

                <Box sx={{
                    gridColumn: 'span 4',
                    display: 'grid',
                    gridTemplateColumns: 'inherit',
                    '& > div': {
                        color: '#595959',
                        padding: '8px',
                        fontWeight: 600,
                    }
                }}>
                    {records.map((record, index) => {
                        const key = 'content_id' in record ? record.content_id : record.user_id;
                        const isOdd = index % 2 === 1;
                        const bgColor = isOdd ? '#FAFAFA' : 'var(--color-white)';
                        const totalRecord = record.totals?.find(t => t.period_key === 'total');

                        return (
                            <React.Fragment key={`right-${key}-${index}`}>
                                <Box sx={{backgroundColor: bgColor, borderLeft: '1px solid #8D8D8D'}}>
                                    {formatNumber(totalRecord?.avg_result_rate, ' %', 2, 2)}
                                </Box>
                                <Box sx={{backgroundColor: bgColor}}>
                                    {formatNumber(totalRecord?.avg_study_time_minutes, '', 2, 2)}
                                </Box>
                                <Box sx={{backgroundColor: bgColor}}>
                                    {formatNumber(totalRecord?.total_studies)}
                                </Box>
                                <Box sx={{backgroundColor: bgColor}}>
                                    {formatNumber(totalRecord?.total_step_count)}
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    </Box>;
};
